
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useTimeSlotPicker } from "@/hooks/useTimeSlotPicker";
import { formatTimeFromDate, convertTo12HourFormat, parseStoredDateTime } from "@/utils/timeUtils";
import { format } from "date-fns";

interface TimeSlotPickerProps {
  startTime: string;
  endTime: string;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  isAuthenticated: boolean;
  selectedTimeSlot: string;
  operatingHours?: any[];
  selectedDate?: Date;
  bookings?: any[];
}

export const TimeSlotPicker = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  isAuthenticated,
  selectedTimeSlot,
  operatingHours,
  selectedDate,
  bookings = []
}: TimeSlotPickerProps) => {
  // Calculate start and end hours from operating hours
  const getTimeRangeFromOperatingHours = () => {
    if (!operatingHours || operatingHours.length === 0 || !selectedDate) {
      return { startHour: 7, endHour: 20 }; // Default fallback
    }

    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const todaysHours = operatingHours.find(hour => 
      hour.daysOfWeek.includes(dayOfWeek)
    );

    if (!todaysHours) {
      return { startHour: 7, endHour: 20 }; // Default fallback if no hours for today
    }

    // Parse start and end times
    const [startHours] = todaysHours.startTime.split(':').map(Number);
    const [endHours] = todaysHours.endTime.split(':').map(Number);

    return { startHour: startHours, endHour: endHours };
  };

  const { startHour, endHour } = getTimeRangeFromOperatingHours();

  const { timeSlots, availableEndTimes, handleStartTimeChange } = useTimeSlotPicker({
    startTime,
    onStartTimeChange,
    onEndTimeChange,
    selectedTimeSlot,
    startHour,
    endHour
  });

  // Create a map of time slots to their booking status
  const getBookingStatusForTime = (timeSlot: string) => {
    if (!bookings || !selectedDate) return null;
    
    // Convert time slot to check against bookings
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const checkTime = new Date(selectedDate);
    checkTime.setHours(hours, minutes, 0, 0);
    
    // Find booking that overlaps with this time slot
    for (const booking of bookings) {
      const startTime = parseStoredDateTime(booking.booking_date || booking.start_time, booking.start_time_local);
      const endTime = parseStoredDateTime(booking.booking_date || booking.end_time, booking.end_time_local);
      
      if (checkTime >= startTime && checkTime < endTime) {
        return booking.status;
      }
    }
    
    return null;
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;
    
    const statusConfig = {
      'blocked': { label: 'Blocked', variant: 'secondary' as const },
      'travel': { label: 'Travel', variant: 'secondary' as const },
      'new': { label: 'Pending', variant: 'default' as const },
      'accepted': { label: 'Booked', variant: 'default' as const },
      'confirmed': { label: 'Confirmed', variant: 'default' as const },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;
    
    return (
      <Badge variant={config.variant} className="ml-2 text-xs">
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="start-time">Start Time</Label>
          <Select
            value={startTime || undefined}
            onValueChange={handleStartTimeChange}
            disabled={!isAuthenticated}
          >
            <SelectTrigger id="start-time">
              <SelectValue placeholder="Select start time" />
            </SelectTrigger>
            <SelectContent>
              {timeSlots.map((time) => {
                const status = getBookingStatusForTime(time);
                return (
                  <SelectItem key={time} value={time} className="flex items-center justify-between">
                    <div className="flex items-center justify-between w-full">
                      <span>{convertTo12HourFormat(time)}</span>
                      {getStatusBadge(status)}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="end-time">End Time</Label>
          <Select
            value={endTime || undefined}
            onValueChange={onEndTimeChange}
            disabled={!isAuthenticated || !startTime || availableEndTimes.length === 0}
          >
            <SelectTrigger id="end-time">
              <SelectValue placeholder="Select end time" />
            </SelectTrigger>
            <SelectContent>
              {availableEndTimes.map((time) => {
                const status = getBookingStatusForTime(time);
                return (
                  <SelectItem key={time} value={time} className="flex items-center justify-between">
                    <div className="flex items-center justify-between w-full">
                      <span>{convertTo12HourFormat(time)}</span>
                      {getStatusBadge(status)}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
